import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, inr, formatApiError } from "../lib/api";
import { useStore } from "../context/StoreContext";
import { toast } from "sonner";
import ProductEditor from "../components/ProductEditor";
import ImageUploader from "../components/ImageUploader";
import { OCCASIONS, FESTIVALS } from "../data/taxonomy";

const TABS = ["Overview", "Customers", "Orders", "Products", "Homepage", "Images", "Content", "Coupons", "FAQs", "Pincodes", "Contest", "Leads", "Reviews", "Settings"];
const ORDER_STATUSES = ["Payment Successful", "Order Placed", "Processing", "Packed", "Shipped", "Out for Delivery", "Delivered", "Pending", "Confirmed", "Cancelled", "Refunded"];

export default function Admin() {
  const { user } = useStore();
  const navigate = useNavigate();
  const [tab, setTab] = useState("Overview");
  const [stats, setStats] = useState(null);
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [coupons, setCoupons] = useState([]);
  const [faqs, setFaqs] = useState([]);
  const [pincodes, setPincodes] = useState([]);
  const [leads, setLeads] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [settings, setSettings] = useState(null);
  const [editing, setEditing] = useState(null);
  const [edits, setEdits] = useState({});
  const [expandedOrder, setExpandedOrder] = useState(null);

  useEffect(() => {
    if (user === null || (user && user.role !== "admin")) navigate("/");
  }, [user]);

  const load = async () => {
    try {
      const [s, o, p, c, f, pi, l, r, st] = await Promise.all([
        api.get("/admin/stats"), api.get("/admin/orders"), api.get("/products"),
        api.get("/admin/coupons"), api.get("/admin/faqs"), api.get("/admin/pincodes"),
        api.get("/admin/leads"), api.get("/admin/reviews"), api.get("/settings"),
      ]);
      setStats(s.data); setOrders(o.data.orders); setProducts(p.data.products); setCoupons(c.data.coupons);
      setFaqs(f.data.faqs); setPincodes(pi.data.pincodes); setLeads(l.data); setReviews(r.data.reviews); setSettings(st.data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  useEffect(() => { if (user?.role === "admin") load(); }, [user]);
  if (!user || user.role !== "admin") return null;

  const saveProduct = async (slug) => {
    try {
      await api.patch(`/admin/products/${slug}`, edits[slug]);
      toast.success("Product updated");
      setEdits({ ...edits, [slug]: {} });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const e = (slug, k, v) => setEdits({ ...edits, [slug]: { ...(edits[slug] || {}), [k]: v } });
  const val = (p, k) => edits[p.slug]?.[k] ?? p[k];

  const input = "bg-[#FFFFFF] border border-[#7E1E3F]/25 rounded-lg px-3 py-2 text-sm text-[#2E1A22] outline-none focus:border-[#7E1E3F]/60";

  return (
    <div className="pt-28 pb-24 max-w-7xl mx-auto px-4 sm:px-6 min-h-screen" data-testid="admin-dashboard">
      <h1 className="font-serif-lux text-4xl text-[#2E1A22]">Admin <span className="gold-text">Suite</span></h1>
      <div className="flex gap-2 mt-8 mb-8 overflow-x-auto pb-2" style={{ scrollbarWidth: "none" }}>
        {TABS.map((t) => (
          <button key={t} onClick={() => setTab(t)}
            className={`shrink-0 text-[11px] tracking-[0.15em] uppercase rounded-full px-4 py-2 border transition-colors ${tab === t ? "bg-[#7E1E3F] text-[#FFFFFF] border-[#7E1E3F] font-semibold" : "border-[#7E1E3F]/25 text-[#857078] hover:border-[#7E1E3F]/60"}`}
            data-testid={`admin-tab-${t.toLowerCase()}`}>{t}</button>
        ))}
      </div>

      {tab === "Overview" && stats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="admin-stats">
          {[["Revenue", inr(stats.revenue)], ["Orders", stats.orders], ["Customers", stats.customers], ["Avg Order Value", inr(stats.aov)],
            ["Notify Leads", stats.notify_leads], ["Contact Msgs", stats.contact_messages], ["Corporate Enquiries", stats.corporate_enquiries], ["Pending Reviews", stats.pending_reviews]].map(([k, v]) => (
            <div key={k} className="card-surface rounded-2xl p-5">
              <p className="text-[11px] font-mono-lux tracking-[0.2em] uppercase text-[#857078]">{k}</p>
              <p className="font-serif-lux text-3xl text-[#2E1A22] mt-2">{v}</p>
            </div>
          ))}
        </div>
      )}

      {tab === "Orders" && (
        <div className="space-y-3" data-testid="admin-orders">
          {orders.length === 0 && <p className="text-[#857078]">No orders yet.</p>}
          {orders.map((o) => (
            <div key={o.order_id} className="card-surface rounded-xl p-4" data-testid={`admin-order-${o.order_id}`}>
              <div className="flex flex-wrap items-center gap-4">
                <div className="flex-1 min-w-[220px]">
                  <p className="font-mono-lux text-sm text-[#7E1E3F]">{o.order_id}
                    <span className={`ml-2 text-[10px] tracking-widest uppercase px-2 py-0.5 rounded-full ${o.payment.status === "paid" ? "bg-[#2A9D8F]/15 text-[#2A9D8F]" : o.payment.status === "failed" ? "bg-[#E63946]/10 text-[#E63946]" : "bg-[#B4853B]/15 text-[#B4853B]"}`} data-testid={`admin-order-paystatus-${o.order_id}`}>{o.payment.status === "created" ? "Payment Pending" : o.payment.status}</span>
                  </p>
                  <p className="text-xs text-[#857078] mt-1">{o.address?.name || "Guest"} · {o.email}{o.address?.phone ? ` · ${o.address.phone}` : ""}</p>
                  <p className="text-xs text-[#857078] mt-0.5">{o.items.map((i) => `${i.name} ×${i.qty}`).join(", ")} · <span className="text-[#2E1A22] font-medium">{inr(o.total)}</span></p>
                  <p className="text-[11px] text-[#857078]/70 mt-0.5" data-testid={`admin-order-date-${o.order_id}`}>{o.created_at ? new Date(o.created_at).toLocaleString("en-IN", { day: "numeric", month: "short", year: "numeric", hour: "numeric", minute: "2-digit" }) : "—"}</p>
                </div>
                <button onClick={() => setExpandedOrder(expandedOrder === o.order_id ? null : o.order_id)}
                  className="text-xs font-semibold tracking-wider uppercase border border-[#7E1E3F]/40 text-[#5C1230] rounded-full px-5 py-2 hover:bg-[#7E1E3F]/10"
                  data-testid={`admin-order-details-${o.order_id}`}>
                  {expandedOrder === o.order_id ? "Hide Details" : "View Details"}
                </button>
                <select value={o.status} onChange={async (ev) => { await api.patch(`/admin/orders/${o.order_id}`, { status: ev.target.value }); toast.success("Status updated"); load(); }}
                  className={input} data-testid={`admin-order-status-${o.order_id}`}>
                  {ORDER_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div className="mt-3 pt-3 border-t border-[#7E1E3F]/10 flex flex-wrap gap-x-8 gap-y-1 text-[11px] font-mono-lux text-[#857078]" data-testid={`admin-order-payinfo-${o.order_id}`}>
                <p>Mode: <span className="text-[#45303A]">{o.payment.mode === "razorpay" ? "Razorpay" : "Demo"}</span></p>
                <p>Razorpay Order ID: <span className="text-[#45303A]">{o.razorpay_order_id || "—"}</span></p>
                <p>Razorpay Payment ID: <span className="text-[#45303A]">{o.payment.razorpay_payment_id || "—"}</span></p>
                {o.payment.paid_at && <p>Paid At: <span className="text-[#45303A]">{new Date(o.payment.paid_at).toLocaleString("en-IN")}</span></p>}
              </div>
              {expandedOrder === o.order_id && (
                <div className="mt-3 pt-3 border-t border-[#7E1E3F]/10 grid sm:grid-cols-2 gap-5" data-testid={`admin-order-full-${o.order_id}`}>
                  <div>
                    <p className="text-[10px] font-mono-lux tracking-[0.15em] uppercase text-[#857078] mb-1.5">Delivery Address</p>
                    <p className="text-sm text-[#2E1A22] font-medium">{o.address?.name}</p>
                    <p className="text-xs text-[#45303A] mt-0.5">{o.address?.line1}{o.address?.line2 ? `, ${o.address.line2}` : ""}</p>
                    <p className="text-xs text-[#45303A]">{o.address?.city}, {o.address?.state} – {o.address?.pincode}</p>
                    <p className="text-xs text-[#857078] mt-1.5">Phone: {o.address?.phone || "—"}</p>
                    <p className="text-xs text-[#857078]">Email: {o.email}</p>
                    {o.gift_message && (
                      <p className="text-xs text-[#7E1E3F] mt-2 italic">Gift message: "{o.gift_message.message}" (To {o.gift_message.recipient}, from {o.gift_message.sender})</p>
                    )}
                  </div>
                  <div>
                    <p className="text-[10px] font-mono-lux tracking-[0.15em] uppercase text-[#857078] mb-1.5">Amount Breakup</p>
                    <div className="text-xs text-[#45303A] space-y-1">
                      {o.items.map((i) => (
                        <div key={i.slug + (i.personalization?.name || "")}>
                          <p className="flex justify-between gap-4"><span>{i.name} × {i.qty}</span><span>{inr(i.line_total)}</span></p>
                          {i.personalization && (
                            <p className="text-[11px] text-[#B4853B] flex items-center gap-1.5 mt-0.5" data-testid={`admin-pers-${o.order_id}-${i.slug}`}>
                              {i.personalization.photo && <a href={i.personalization.photo} target="_blank" rel="noreferrer"><img src={i.personalization.photo} alt="Personalization" className="w-6 h-6 rounded object-cover border border-[#7E1E3F]/25" /></a>}
                              Personalized for “{i.personalization.name}”{i.personalization.message ? ` — “${i.personalization.message}”` : ""}{i.personalization.photo ? " · photo attached (click to view)" : ""}
                            </p>
                          )}
                        </div>
                      ))}
                      <p className="flex justify-between text-[#857078] border-t border-[#E5E7EB] pt-1"><span>Subtotal</span><span>{inr(o.subtotal)}</span></p>
                      {o.discount > 0 && <p className="flex justify-between text-[#2A9D8F]"><span>Discount {o.coupon && `(${o.coupon})`}</span><span>−{inr(o.discount)}</span></p>}
                      {o.gift_wrap && <p className="flex justify-between text-[#857078]"><span>Gift Wrapping</span><span>{inr(o.gift_wrap_fee)}</span></p>}
                      <p className="flex justify-between text-[#857078]"><span>Delivery</span><span>{o.shipping_fee ? inr(o.shipping_fee) : "Free"}</span></p>
                      <p className="flex justify-between text-sm text-[#2E1A22] font-semibold border-t border-[#E5E7EB] pt-1"><span>Total</span><span>{inr(o.total)}</span></p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {tab === "Products" && (
        <div className="space-y-3" data-testid="admin-products">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <p className="text-xs text-[#857078]">Full control: edit every field, manage images, variants, SEO and availability. Changes go live instantly — no redeployment.</p>
            <button onClick={() => setEditing({})} className="text-xs font-semibold tracking-wider uppercase bg-[#7E1E3F] text-white rounded-full px-6 py-2.5 hover:bg-[#5C1230] transition-colors" data-testid="admin-new-product">+ New Product</button>
          </div>
          {products.map((p) => (
            <div key={p.slug} className="card-surface rounded-xl p-4 flex flex-wrap items-center gap-3" data-testid={`admin-product-${p.slug}`}>
              <img src={p.images[0]} alt="" className="w-12 h-14 object-contain bg-white rounded-lg border border-[#E5E7EB]" />
              <div className="flex-1 min-w-[200px]">
                <p className="text-sm text-[#2E1A22] font-medium">{p.name}</p>
                <p className="text-[11px] text-[#857078]">{p.category} · {inr(p.price)}{p.stock !== undefined ? ` · stock ${p.stock ?? "—"}` : ""}{p.sku ? ` · SKU ${p.sku}` : ""}</p>
              </div>
              <span className={`text-[10px] font-mono-lux tracking-widest uppercase px-2.5 py-1 rounded-full ${p.availability === "available" ? "bg-[#2A9D8F]/15 text-[#2A9D8F]" : p.availability === "out_of_stock" ? "bg-[#E63946]/10 text-[#E63946]" : "bg-[#7E1E3F]/15 text-[#5C1230]"}`}>
                {p.availability.replace("_", " ")}
              </span>
              <button onClick={() => setEditing(p)} className="text-xs font-semibold tracking-wider uppercase border border-[#7E1E3F]/50 text-[#5C1230] rounded-full px-5 py-2 hover:bg-[#7E1E3F]/10" data-testid={`admin-edit-${p.slug}`}>Edit</button>
              <button onClick={async () => { if (window.confirm(`Delete "${p.name}" permanently?`)) { await api.delete(`/admin/products/${p.slug}`); toast.success("Product deleted"); load(); } }}
                className="text-xs text-[#E63946] border border-[#E63946]/40 rounded-full px-4 py-2 hover:bg-[#E63946]/10" data-testid={`admin-delete-${p.slug}`}>Delete</button>
            </div>
          ))}
        </div>
      )}

      {tab === "Homepage" && settings && <HomepageTab settings={settings} reload={load} input={input} />}
      {tab === "Images" && settings && <ImagesTab settings={settings} reload={load} input={input} />}
      {tab === "Content" && settings && <ContentTab settings={settings} reload={load} input={input} />}
      {tab === "Customers" && <CustomersTab input={input} />}

      {tab === "Coupons" && <CouponsTab coupons={coupons} reload={load} input={input} />}
      {tab === "FAQs" && <FaqsTab faqs={faqs} reload={load} input={input} />}
      {tab === "Pincodes" && <PincodesTab pincodes={pincodes} reload={load} input={input} />}
      {tab === "Contest" && <ContestTab input={input} />}

      {tab === "Leads" && leads && (
        <div className="grid lg:grid-cols-3 gap-6" data-testid="admin-leads">
          {[["Notify Me Waitlist", leads.notify, (x) => `${x.email} → ${x.product_slug}`],
            ["Contact Messages", leads.contacts, (x) => `${x.name} (${x.email}): ${x.message?.slice(0, 80)}`],
            ["Corporate Enquiries", leads.corporate, (x) => `${x.company} · ${x.email} · qty ${x.quantity}`]].map(([title, items, fmt]) => (
            <div key={title} className="card-surface rounded-2xl p-5">
              <p className="font-mono-lux text-[11px] uppercase tracking-[0.2em] text-[#7E1E3F] mb-4">{title} ({items.length})</p>
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {items.length === 0 && <p className="text-xs text-[#857078]">None yet.</p>}
                {items.map((x, i) => <p key={i} className="text-xs text-[#857078] border-b border-[#7E1E3F]/10 pb-2">{fmt(x)}</p>)}
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === "Reviews" && (
        <div className="space-y-3" data-testid="admin-reviews">
          {reviews.length === 0 && <p className="text-[#857078]">No reviews yet. Only verified buyers can review.</p>}
          {reviews.map((r) => (
            <div key={r.id} className="card-surface rounded-xl p-4 flex items-center gap-4">
              <div className="flex-1">
                <p className="text-sm text-[#45303A]">{"★".repeat(r.rating)} · {r.text}</p>
                <p className="text-xs text-[#857078] mt-1">{r.user_name} · {r.product_slug} · {r.approved ? "Approved" : "Pending"}</p>
              </div>
              <button onClick={async () => { await api.patch(`/admin/reviews/${r.id}`, { approved: !r.approved }); load(); }}
                className="text-xs border border-[#7E1E3F]/40 text-[#5C1230] rounded-full px-4 py-2" data-testid={`admin-review-${r.id}`}>
                {r.approved ? "Unpublish" : "Approve"}
              </button>
              <button onClick={async () => { if (window.confirm("Delete this review permanently?")) { await api.delete(`/admin/reviews/${r.id}`); toast.success("Review deleted"); load(); } }}
                className="text-xs text-[#E63946] border border-[#E63946]/40 rounded-full px-4 py-2 hover:bg-[#E63946]/10" data-testid={`admin-review-del-${r.id}`}>
                Delete
              </button>
            </div>
          ))}
        </div>
      )}

      {tab === "Settings" && settings && <SettingsTab settings={settings} reload={load} input={input} />}

      {editing !== null && (
        <ProductEditor product={editing.slug ? editing : null} onClose={() => setEditing(null)} onSaved={load} />
      )}
    </div>
  );
}

function ContestTab({ input }) {
  const [entries, setEntries] = useState([]);
  const [count, setCount] = useState(0);
  const [q, setQ] = useState("");

  const load = (query = q) =>
    api.get(`/admin/contest${query ? `?q=${encodeURIComponent(query)}` : ""}`)
      .then((r) => { setEntries(r.data.entries); setCount(r.data.count); })
      .catch((e) => toast.error(formatApiError(e)));

  useEffect(() => { load(""); }, []);

  const exportCsv = async () => {
    const res = await api.get("/admin/contest/export", { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = "savyron-contest-entries.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div data-testid="admin-contest">
      <div className="flex flex-wrap gap-3 items-center mb-6">
        <input className={`${input} w-64`} placeholder="Search name, email, Instagram, pincode" value={q}
          onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()}
          data-testid="contest-search" />
        <button onClick={() => load()} className="text-xs font-semibold uppercase tracking-wider border border-[#7E1E3F]/50 text-[#5C1230] rounded-full px-5 py-2.5 hover:bg-[#7E1E3F]/10" data-testid="contest-search-btn">Search</button>
        <button onClick={exportCsv} className="text-xs font-semibold uppercase tracking-wider bg-[#7E1E3F] text-white rounded-full px-5 py-2.5 hover:bg-[#5C1230]" data-testid="contest-export">Export CSV</button>
        <span className="text-xs text-[#857078]" data-testid="contest-count">{count} entr{count === 1 ? "y" : "ies"}</span>
      </div>
      <div className="space-y-2">
        {entries.length === 0 && <p className="text-[#857078] text-sm">No contest entries yet.</p>}
        {entries.map((en) => (
          <div key={en.id} className="card-surface rounded-xl p-4 flex flex-wrap gap-x-6 gap-y-1 items-center" data-testid={`contest-entry-${en.id}`}>
            <p className="text-sm text-[#2E1A22] font-medium min-w-[140px]">{en.name}</p>
            <p className="text-xs text-[#857078]">{en.email}</p>
            <p className="text-xs text-[#7E1E3F]">{en.instagram}</p>
            <p className="text-xs text-[#857078]">{en.pincode}</p>
            <p className="text-[11px] text-[#857078]/70 ml-auto">{new Date(en.created_at).toLocaleString("en-IN")}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function CouponsTab({ coupons, reload, input }) {
  const [form, setForm] = useState({ code: "", type: "percent", value: 10, min_order: 0, label: "" });
  return (
    <div data-testid="admin-coupons">
      <div className="card-surface rounded-xl p-5 flex flex-wrap gap-3 items-end mb-6">
        <label className="text-[10px] text-[#857078]">Code<input className={`${input} block w-32`} value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} data-testid="coupon-code-input" /></label>
        <label className="text-[10px] text-[#857078]">Type
          <select className={`${input} block`} value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} data-testid="coupon-type">
            <option value="percent">Percent %</option><option value="flat">Flat ₹</option>
          </select></label>
        <label className="text-[10px] text-[#857078]">Value<input type="number" className={`${input} block w-24`} value={form.value} onChange={(e) => setForm({ ...form, value: +e.target.value })} data-testid="coupon-value" /></label>
        <label className="text-[10px] text-[#857078]">Min Order ₹<input type="number" className={`${input} block w-24`} value={form.min_order} onChange={(e) => setForm({ ...form, min_order: +e.target.value })} data-testid="coupon-min" /></label>
        <label className="text-[10px] text-[#857078] flex-1 min-w-[180px]">Label<input className={`${input} block w-full`} value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} data-testid="coupon-label" /></label>
        <button onClick={async () => { await api.post("/admin/coupons", form); toast.success("Coupon saved"); setForm({ code: "", type: "percent", value: 10, min_order: 0, label: "" }); reload(); }}
          className="text-xs font-semibold uppercase tracking-wider bg-[#7E1E3F] text-[#FFFFFF] rounded-full px-6 py-2.5" data-testid="coupon-save">Save</button>
      </div>
      <div className="space-y-2">
        {coupons.map((c) => (
          <div key={c.code} className="card-surface rounded-xl p-4 flex items-center gap-4 flex-wrap">
            <p className="font-mono-lux text-[#7E1E3F]">{c.code}</p>
            <p className="text-xs text-[#857078]">{c.type === "percent" ? `${c.value}% off` : `₹${c.value} off`} · min {inr(c.min_order)} · used {c.used}{c.usage_limit ? `/${c.usage_limit}` : ""}</p>
            <p className="text-xs text-[#857078] flex-1">{c.label}</p>
            <button onClick={async () => { await api.post("/admin/coupons", { ...c, active: !c.active }); reload(); }}
              className="text-xs border border-[#7E1E3F]/40 text-[#5C1230] rounded-full px-4 py-1.5" data-testid={`coupon-toggle-${c.code}`}>
              {c.active ? "Disable" : "Enable"}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

function FaqsTab({ faqs, reload, input }) {
  const [form, setForm] = useState({ question: "", answer: "", category: "orders" });
  return (
    <div data-testid="admin-faqs">
      <div className="card-surface rounded-xl p-5 space-y-3 mb-6">
        <input className={`${input} w-full`} placeholder="Question" value={form.question} onChange={(e) => setForm({ ...form, question: e.target.value })} data-testid="faq-q-input" />
        <textarea className={`${input} w-full`} rows={2} placeholder="Answer" value={form.answer} onChange={(e) => setForm({ ...form, answer: e.target.value })} data-testid="faq-a-input" />
        <button onClick={async () => { await api.post("/admin/faqs", form); toast.success("FAQ added"); setForm({ question: "", answer: "", category: "orders" }); reload(); }}
          className="text-xs font-semibold uppercase tracking-wider bg-[#7E1E3F] text-[#FFFFFF] rounded-full px-6 py-2.5" data-testid="faq-save">Add FAQ</button>
      </div>
      <div className="space-y-2">
        {faqs.map((f) => (
          <div key={f.id} className="card-surface rounded-xl p-4 flex items-center gap-4">
            <div className="flex-1">
              <p className="text-sm text-[#45303A]">{f.question}</p>
              <p className="text-xs text-[#857078] mt-0.5">{f.category}</p>
            </div>
            <button onClick={async () => { await api.post("/admin/faqs", { id: f.id, question: f.question, answer: f.answer, category: f.category, enabled: !f.enabled }); reload(); }}
              className="text-xs border border-[#7E1E3F]/40 text-[#5C1230] rounded-full px-4 py-1.5" data-testid={`faq-toggle-${f.id}`}>
              {f.enabled ? "Disable" : "Enable"}
            </button>
            <button onClick={async () => { await api.delete(`/admin/faqs/${f.id}`); reload(); }}
              className="text-xs text-[#E63946] border border-[#E63946]/40 rounded-full px-4 py-1.5" data-testid={`faq-delete-${f.id}`}>Delete</button>
          </div>
        ))}
      </div>
    </div>
  );
}

function PincodesTab({ pincodes, reload, input }) {
  const [pin, setPin] = useState("");
  return (
    <div data-testid="admin-pincodes">
      <div className="card-surface rounded-xl p-5 flex gap-3 items-end mb-6 flex-wrap">
        <label className="text-[10px] text-[#857078]">Add serviceable pincode
          <input className={`${input} block w-40`} maxLength={6} value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))} data-testid="pincode-add-input" /></label>
        <button onClick={async () => { try { await api.post("/admin/pincodes", { pincode: pin }); toast.success("Pincode added"); setPin(""); reload(); } catch (err) { toast.error(formatApiError(err)); } }}
          className="text-xs font-semibold uppercase tracking-wider bg-[#7E1E3F] text-[#FFFFFF] rounded-full px-6 py-2.5" data-testid="pincode-add-btn">Add</button>
        <p className="text-xs text-[#857078]">Any pincode not listed here is treated as non-serviceable. Nothing is invented.</p>
      </div>
      <div className="flex flex-wrap gap-2">
        {pincodes.map((p) => (
          <span key={p.pincode} className="flex items-center gap-2 text-xs border border-[#7E1E3F]/25 rounded-full px-3 py-1.5 text-[#45303A]">
            {p.pincode}
            <button onClick={async () => { await api.delete(`/admin/pincodes/${p.pincode}`); reload(); }} className="text-[#E63946]" data-testid={`pincode-del-${p.pincode}`}>×</button>
          </span>
        ))}
      </div>
    </div>
  );
}

function SettingsTab({ settings, reload, input }) {
  const [form, setForm] = useState({ gift_wrap_price: settings.gift_wrap_price, delivery_estimate: settings.delivery_estimate,
    contest_enabled: settings.contest_enabled !== false, contest_prize_note: settings.contest_prize_note || "",
    support_email: settings.support_email || "", instagram: settings.instagram || "", address: settings.address || "",
    policies_text: settings.policies_text || "", site_title: settings.site_title || "", site_description: settings.site_description || "" });
  return (
    <div className="card-surface rounded-2xl p-6 max-w-lg space-y-4" data-testid="admin-settings">
      <label className="block text-[10px] text-[#857078]">Gift Wrapping Price (₹)
        <input type="number" className={`${input} block w-40 mt-1`} value={form.gift_wrap_price} onChange={(e) => setForm({ ...form, gift_wrap_price: +e.target.value })} data-testid="settings-wrap-price" /></label>
      <label className="block text-[10px] text-[#857078]">Delivery Estimate Text
        <input className={`${input} block w-full mt-1`} value={form.delivery_estimate} onChange={(e) => setForm({ ...form, delivery_estimate: e.target.value })} data-testid="settings-delivery" /></label>
      <label className="flex items-center gap-3 text-sm text-[#45303A]">
        <input type="checkbox" className="accent-[#7E1E3F] w-4 h-4" checked={form.contest_enabled} onChange={(e) => setForm({ ...form, contest_enabled: e.target.checked })} data-testid="settings-contest-enabled" />
        Contest open (show Participate &amp; Win section)
      </label>
      <label className="block text-[10px] text-[#857078]">Contest Prize Note (optional — shown only if set)
        <input className={`${input} block w-full mt-1`} placeholder="e.g. Win a Savyron Signature Hamper" value={form.contest_prize_note} onChange={(e) => setForm({ ...form, contest_prize_note: e.target.value })} data-testid="settings-contest-note" /></label>
      <div className="grid grid-cols-2 gap-3">
        <label className="block text-[10px] text-[#857078]">Support Email
          <input className={`${input} block w-full mt-1`} value={form.support_email} onChange={(e) => setForm({ ...form, support_email: e.target.value })} data-testid="settings-email" /></label>
        <label className="block text-[10px] text-[#857078]">Instagram URL
          <input className={`${input} block w-full mt-1`} value={form.instagram} onChange={(e) => setForm({ ...form, instagram: e.target.value })} data-testid="settings-instagram" /></label>
      </div>
      <label className="block text-[10px] text-[#857078]">Registered Address
        <input className={`${input} block w-full mt-1`} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="settings-address" /></label>
      <label className="block text-[10px] text-[#857078]">Store Policies (shown on FAQ page)
        <textarea rows={4} className={`${input} block w-full mt-1`} value={form.policies_text} onChange={(e) => setForm({ ...form, policies_text: e.target.value })} data-testid="settings-policies" /></label>
      <div className="grid grid-cols-2 gap-3">
        <label className="block text-[10px] text-[#857078]">Site SEO Title
          <input className={`${input} block w-full mt-1`} value={form.site_title} onChange={(e) => setForm({ ...form, site_title: e.target.value })} data-testid="settings-seo-title" /></label>
        <label className="block text-[10px] text-[#857078]">Site Meta Description
          <input className={`${input} block w-full mt-1`} value={form.site_description} onChange={(e) => setForm({ ...form, site_description: e.target.value })} data-testid="settings-seo-desc" /></label>
      </div>
      <button onClick={async () => { await api.put("/admin/settings", form); toast.success("Settings saved — live immediately"); reload(); }}
        className="text-xs font-semibold uppercase tracking-wider bg-[#7E1E3F] text-white rounded-full px-6 py-2.5" data-testid="settings-save">Save Settings</button>
      <p className="text-xs text-[#857078]">Support: founder@savyron.in · Instagram: @savyron.in · Plot No. 87, Sector 62, Noida – 201309</p>
    </div>
  );
}

function HomepageTab({ settings, reload, input }) {
  const [form, setForm] = useState({
    hero_title: settings.hero_title || "", hero_subtitle: settings.hero_subtitle || "",
    hero_cta_primary: settings.hero_cta_primary || "", hero_cta_secondary: settings.hero_cta_secondary || "",
    hero_image: settings.hero_image || "", offers_note: settings.offers_note || "",
    contest_enabled: settings.contest_enabled !== false, contest_title: settings.contest_title || "",
    contest_subtitle: settings.contest_subtitle || "", contest_prize_note: settings.contest_prize_note || "",
    contest_rules: settings.contest_rules || "", contest_deadline: settings.contest_deadline || "",
  });
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const save = async () => {
    await api.put("/admin/settings", form);
    toast.success("Homepage published — live immediately");
    reload();
  };
  return (
    <div className="grid lg:grid-cols-2 gap-6" data-testid="admin-homepage">
      <div className="card-surface rounded-2xl p-6 space-y-4">
        <h3 className="font-serif-lux text-xl text-[#2E1A22]">Hero Section</h3>
        <label className="block text-[10px] text-[#857078]">Hero Headline (one line per row — leave empty for default)
          <textarea rows={3} className={`${input} block w-full mt-1`} placeholder={"Gifts That Make\nMoments\nMemorable"} value={form.hero_title} onChange={(e) => set("hero_title", e.target.value)} data-testid="hp-hero-title" /></label>
        <label className="block text-[10px] text-[#857078]">Hero Subheading
          <input className={`${input} block w-full mt-1`} placeholder="Thoughtfully chosen gifts for every special occasion." value={form.hero_subtitle} onChange={(e) => set("hero_subtitle", e.target.value)} data-testid="hp-hero-sub" /></label>
        <div className="grid grid-cols-2 gap-3">
          <label className="block text-[10px] text-[#857078]">Primary Button
            <input className={`${input} block w-full mt-1`} placeholder="Explore Gifts" value={form.hero_cta_primary} onChange={(e) => set("hero_cta_primary", e.target.value)} data-testid="hp-cta-1" /></label>
          <label className="block text-[10px] text-[#857078]">Secondary Button
            <input className={`${input} block w-full mt-1`} placeholder="Shop Sonata" value={form.hero_cta_secondary} onChange={(e) => set("hero_cta_secondary", e.target.value)} data-testid="hp-cta-2" /></label>
        </div>
        <div>
          <span className="text-[10px] text-[#857078] block mb-1">Hero Product Image</span>
          <div className="flex items-center gap-3">
            {form.hero_image && <img src={form.hero_image} alt="" className="w-16 h-16 object-contain bg-white rounded-lg border border-[#E5E7EB]" />}
            <ImageUploader label={form.hero_image ? "Replace Hero Image" : "Upload Hero Image"} aspect={1} testid="hp-hero-image" onUploaded={(url) => set("hero_image", url)} />
          </div>
          <p className="text-[10px] text-[#857078] mt-1">Default: the real Sonata front shot.</p>
        </div>
        <label className="block text-[10px] text-[#857078]">Offers Page Note
          <input className={`${input} block w-full mt-1`} placeholder="Shown on the Offers page banner" value={form.offers_note} onChange={(e) => set("offers_note", e.target.value)} data-testid="hp-offers-note" /></label>
      </div>
      <div className="card-surface rounded-2xl p-6 space-y-4">
        <h3 className="font-serif-lux text-xl text-[#2E1A22]">Contest (Participate &amp; Win)</h3>
        <label className="flex items-center gap-3 text-sm text-[#45303A]">
          <input type="checkbox" className="accent-[#7E1E3F] w-4 h-4" checked={form.contest_enabled} onChange={(e) => set("contest_enabled", e.target.checked)} data-testid="hp-contest-enabled" />
          Contest visible on homepage
        </label>
        <label className="block text-[10px] text-[#857078]">Contest Title
          <input className={`${input} block w-full mt-1`} placeholder="Participate to Win Exciting Prizes!" value={form.contest_title} onChange={(e) => set("contest_title", e.target.value)} data-testid="hp-contest-title" /></label>
        <label className="block text-[10px] text-[#857078]">Contest Description
          <textarea rows={2} className={`${input} block w-full mt-1`} placeholder="Enter the Savyron contest for a chance to win…" value={form.contest_subtitle} onChange={(e) => set("contest_subtitle", e.target.value)} data-testid="hp-contest-sub" /></label>
        <label className="block text-[10px] text-[#857078]">Prize (shown only if set)
          <input className={`${input} block w-full mt-1`} placeholder="e.g. Win a Savyron Signature Hamper" value={form.contest_prize_note} onChange={(e) => set("contest_prize_note", e.target.value)} data-testid="hp-contest-prize" /></label>
        <label className="block text-[10px] text-[#857078]">Deadline (shown only if set)
          <input className={`${input} block w-full mt-1`} placeholder="e.g. Entries close 30 Nov 2026" value={form.contest_deadline} onChange={(e) => set("contest_deadline", e.target.value)} data-testid="hp-contest-deadline" /></label>
        <label className="block text-[10px] text-[#857078]">Rules / Entry Requirements (one per line, shown only if set)
          <textarea rows={3} className={`${input} block w-full mt-1`} placeholder={"Follow @savyron.in on Instagram\nOne entry per email address"} value={form.contest_rules} onChange={(e) => set("contest_rules", e.target.value)} data-testid="hp-contest-rules" /></label>
      </div>
      <div className="lg:col-span-2 flex justify-end gap-3">
        <a href="/" target="_blank" rel="noreferrer" className="text-xs font-semibold tracking-wider uppercase border border-[#E5E7EB] text-[#45303A] rounded-full px-6 py-3 hover:border-[#7E1E3F]" data-testid="hp-preview">Preview Live Site</a>
        <button onClick={save} className="text-xs font-semibold tracking-wider uppercase bg-[#7E1E3F] text-white rounded-full px-8 py-3 hover:bg-[#5C1230] transition-colors" data-testid="hp-publish">Publish Homepage Changes</button>
      </div>
    </div>
  );
}


const CONTENT_GROUPS = [
  { title: "Marquee Ribbon", hint: "One phrase per line — the slow scrolling strip near the top.", fields: [["marquee", "Marquee phrases", "area"]] },
  { title: "Occasions Section", fields: [["occ_overline", "Overline"], ["occ_title", "Heading"]] },
  { title: "Festivals Section", fields: [["fest_overline", "Overline"], ["fest_title", "Heading"], ["fest_sub", "Subtext"]] },
  { title: "Recipients Section", fields: [["rec_overline", "Overline"], ["rec_title", "Heading"]] },
  { title: "Gift Finder", fields: [["finder_title", "Heading"]] },
  { title: "Dry Fruits Feature Section", hint: "Highlights: one per line. Image: upload to override the product photo.", fields: [["dry_overline", "Overline"], ["dry_title", "Heading"], ["dry_sub", "Subtext"], ["dry_highlights", "Highlights", "area"], ["dry_cta", "Button Label"]] },
  { title: "Gift Hampers Section", fields: [["hampers_title", "Heading"], ["hampers_sub", "Subtext"]] },
  { title: "Personalized Section", fields: [["pers_title", "Heading"], ["pers_sub", "Subtext"]] },
  { title: "Why Savyron", hint: "One trust point per line (8 shown).", fields: [["why_overline", "Overline"], ["why_title", "Heading"], ["why_items", "Trust points", "area"]] },
  { title: "Savyron Story Chapters", hint: "One chapter per line, format: Chapter Title | Chapter text", fields: [["story_overline", "Overline"], ["story_title", "Heading"], ["story_chapters", "Chapters", "area"]] },
  { title: "Reviews Section", fields: [["reviews_overline", "Overline"], ["reviews_title", "Heading"], ["reviews_empty", "Empty-state quote"], ["reviews_empty_sub", "Empty-state subtext"]] },
  { title: "Corporate Gifting Section", fields: [["corp_overline", "Overline"], ["corp_title", "Heading"], ["corp_sub", "Subtext"], ["corp_cta", "Button Label"]] },
  { title: "Homepage FAQ Section", fields: [["faq_overline", "Overline"], ["faq_title", "Heading"]] },
  { title: "Footer", fields: [["footer_tagline", "Tagline under logo"]] },
];

function ImagesTab({ settings, reload, input }) {
  const [occ, setOcc] = useState({ ...(settings.occasion_images || {}) });
  const [fest, setFest] = useState({ ...(settings.festival_images || {}) });
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  const apply = (setter) => (map) => { setter(map); setDirty(true); };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/settings", { occasion_images: occ, festival_images: fest });
      toast.success("Card images published — live on the site now");
      setDirty(false);
      reload();
    } catch (e) { toast.error(formatApiError(e)); }
    setSaving(false);
  };

  const groups = [
    ["Shop by Occasion", OCCASIONS.slice(0, 8), occ, apply(setOcc), "occ"],
    ["Shop by Festival", FESTIVALS, fest, apply(setFest), "fest"],
  ];

  return (
    <div data-testid="admin-images">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
        <p className="text-xs text-[#857078] max-w-xl">Upload or replace the image of any Occasion or Festival card. Uploads preview instantly here — nothing goes live until you publish. Resetting a card restores its original artwork.</p>
        <div className="flex gap-3 items-center">
          {dirty && <span className="text-[11px] text-[#B4853B] font-medium" data-testid="images-unsaved">Unpublished changes</span>}
          <a href="/" target="_blank" rel="noreferrer" className="text-xs font-semibold tracking-wider uppercase border border-[#E5E7EB] text-[#45303A] rounded-full px-6 py-2.5 hover:border-[#7E1E3F]" data-testid="images-preview">Preview Site</a>
          <button onClick={save} disabled={saving}
            className="text-xs font-semibold tracking-wider uppercase bg-[#7E1E3F] text-white rounded-full px-8 py-2.5 hover:bg-[#5C1230] transition-colors disabled:opacity-60"
            data-testid="images-publish">{saving ? "Publishing…" : "Publish Images"}</button>
        </div>
      </div>
      {groups.map(([title, items, map, setMap, prefix]) => (
        <div key={title} className="mb-10">
          <p className="font-mono-lux text-[11px] uppercase tracking-[0.2em] text-[#7E1E3F] mb-4">{title} ({items.length} cards)</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {items.map((it) => {
              const custom = !!map[it.slug];
              return (
                <div key={it.slug} className="card-surface rounded-xl overflow-hidden" data-testid={`imgmgr-${prefix}-${it.slug}`}>
                  <div className="aspect-[4/5] bg-[#FBF7F1] relative">
                    <img src={map[it.slug] || it.img} alt={it.name} className="w-full h-full object-cover" />
                    {custom && <span className="absolute top-2 left-2 bg-[#B4853B] text-white text-[9px] font-bold tracking-widest uppercase px-2 py-1 rounded-full">Custom</span>}
                  </div>
                  <div className="p-3">
                    <p className="text-xs text-[#2E1A22] font-medium truncate">{it.name}</p>
                    <div className="flex gap-2 mt-2 items-center">
                      <ImageUploader label={custom ? "Replace" : "Upload"} aspect={4 / 5} testid={`imgmgr-upload-${prefix}-${it.slug}`}
                        onUploaded={(url) => setMap({ ...map, [it.slug]: url })} />
                      {custom && (
                        <button onClick={() => { const m = { ...map }; delete m[it.slug]; setMap(m); }}
                          className="text-[11px] text-[#E63946] border border-[#E63946]/40 rounded-full px-3 py-1.5 hover:bg-[#E63946]/10"
                          data-testid={`imgmgr-reset-${prefix}-${it.slug}`}>Reset</button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

function ContentTab({ settings, reload, input }) {
  const [form, setForm] = useState(() => ({ ...(settings.content || {}) }));
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const save = async () => {
    await api.put("/admin/settings", { content: form });
    toast.success("Site content published — live immediately");
    reload();
  };
  return (
    <div data-testid="admin-content">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
        <p className="text-xs text-[#857078]">Edit any text on the homepage. Leave a field empty to keep the default wording. Everything goes live instantly.</p>
        <div className="flex gap-3">
          <a href="/" target="_blank" rel="noreferrer" className="text-xs font-semibold tracking-wider uppercase border border-[#E5E7EB] text-[#45303A] rounded-full px-6 py-2.5 hover:border-[#7E1E3F]" data-testid="content-preview">Preview Site</a>
          <button onClick={save} className="text-xs font-semibold tracking-wider uppercase bg-[#7E1E3F] text-white rounded-full px-8 py-2.5 hover:bg-[#5C1230] transition-colors" data-testid="content-publish">Publish All Changes</button>
        </div>
      </div>
      <div className="grid lg:grid-cols-2 gap-5">
        {CONTENT_GROUPS.map((g) => (
          <div key={g.title} className="card-surface rounded-2xl p-5" data-testid={`content-group-${g.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
            <p className="font-mono-lux text-[11px] uppercase tracking-[0.2em] text-[#7E1E3F] mb-3">{g.title}</p>
            {g.hint && <p className="text-[11px] text-[#857078] mb-3">{g.hint}</p>}
            <div className="space-y-3">
              {g.fields.map(([key, label, kind]) => (
                <label key={key} className="block">
                  <span className="text-[10px] text-[#857078]">{label}</span>
                  {kind === "area" ? (
                    <textarea rows={3} className={`${input} block w-full mt-1`} value={form[key] || ""} onChange={(e) => set(key, e.target.value)} data-testid={`content-${key}`} />
                  ) : (
                    <input className={`${input} block w-full mt-1`} value={form[key] || ""} onChange={(e) => set(key, e.target.value)} data-testid={`content-${key}`} />
                  )}
                </label>
              ))}
              {g.title.startsWith("Dry Fruits") && (
                <div className="flex items-center gap-3 pt-1">
                  {form.dry_image && <img src={form.dry_image} alt="" className="w-14 h-14 object-cover rounded-lg border border-[#E5E7EB]" />}
                  <ImageUploader label={form.dry_image ? "Replace Section Image" : "Upload Section Image"} aspect={4 / 3} testid="content-dry-image" onUploaded={(url) => set("dry_image", url)} />
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}


function CustomersTab({ input }) {
  const [customers, setCustomers] = useState([]);
  const [count, setCount] = useState(0);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [auth, setAuth] = useState("");
  const [sort, setSort] = useState("created");
  const [detail, setDetail] = useState(null);

  const load = (params = {}) => {
    const p = new URLSearchParams();
    const fq = params.q ?? q, fs = params.status ?? status, fa = params.auth ?? auth, fo = params.sort ?? sort;
    if (fq) p.set("q", fq);
    if (fs) p.set("status", fs);
    if (fa) p.set("auth", fa);
    if (fo) p.set("sort", fo);
    api.get(`/admin/customers?${p}`).then((r) => { setCustomers(r.data.customers); setCount(r.data.count); })
      .catch((e) => toast.error(formatApiError(e)));
  };

  useEffect(() => { load({ q: "", status: "", auth: "", sort: "created" }); }, []);

  const openDetail = async (cid) => {
    try {
      const { data } = await api.get(`/admin/customers/${cid}`);
      setDetail(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const toggleStatus = async (c) => {
    const next = c.account_status === "deactivated" ? "active" : "deactivated";
    if (!window.confirm(`${next === "active" ? "Activate" : "Deactivate"} ${c.name}'s account?`)) return;
    await api.patch(`/admin/customers/${c.customer_id}`, { account_status: next });
    toast.success(`Account ${next}`);
    setDetail(null);
    load();
  };

  const badge = (ok, yes, no) => (
    <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${ok ? "bg-[#2A9D8F]/15 text-[#2A9D8F]" : "bg-[#E5E7EB] text-[#857078]"}`}>{ok ? yes : no}</span>
  );

  return (
    <div data-testid="admin-customers">
      <div className="flex flex-wrap gap-3 items-center mb-6">
        <input className={`${input} w-64`} placeholder="Search name, email, phone, ID" value={q}
          onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} data-testid="customer-search" />
        <select className={input} value={status} onChange={(e) => { setStatus(e.target.value); load({ status: e.target.value }); }} data-testid="customer-filter-status">
          <option value="">All Statuses</option><option value="active">Active</option><option value="deactivated">Deactivated</option>
        </select>
        <select className={input} value={auth} onChange={(e) => { setAuth(e.target.value); load({ auth: e.target.value }); }} data-testid="customer-filter-auth">
          <option value="">All Methods</option><option value="password">Email & Password</option><option value="google">Google</option>
        </select>
        <select className={input} value={sort} onChange={(e) => { setSort(e.target.value); load({ sort: e.target.value }); }} data-testid="customer-sort">
          <option value="created">Newest Registered</option><option value="login">Recent Login</option>
        </select>
        <button onClick={() => load()} className="text-xs font-semibold uppercase tracking-wider border border-[#7E1E3F]/50 text-[#5C1230] rounded-full px-5 py-2.5 hover:bg-[#7E1E3F]/10" data-testid="customer-search-btn">Search</button>
        <span className="text-xs text-[#857078]" data-testid="customer-count">{count} customer{count === 1 ? "" : "s"}</span>
      </div>

      <div className="overflow-x-auto card-surface rounded-2xl">
        <table className="w-full text-sm min-w-[900px]" data-testid="customers-table">
          <thead>
            <tr className="text-left text-[10px] font-mono-lux tracking-[0.15em] uppercase text-[#857078] border-b border-[#E5E7EB]">
              {["Customer", "Contact", "Verified", "Method", "Status", "Registered", "Last Login", "Orders", "Total"].map((h) => (
                <th key={h} className="px-4 py-3">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {customers.length === 0 && (
              <tr><td colSpan={9} className="px-4 py-10 text-center text-[#857078]">No customers found.</td></tr>
            )}
            {customers.map((c) => (
              <tr key={c.id} onClick={() => openDetail(c.customer_id || c.id)}
                className="border-b border-[#E5E7EB]/60 hover:bg-[#FBF7F1] cursor-pointer transition-colors" data-testid={`customer-row-${c.customer_id}`}>
                <td className="px-4 py-3">
                  <p className="font-medium text-[#2E1A22]">{c.name}</p>
                  <p className="text-[10px] font-mono-lux text-[#7E1E3F]">{c.customer_id || "—"}</p>
                </td>
                <td className="px-4 py-3">
                  <p className="text-xs text-[#45303A]">{c.email}</p>
                  <p className="text-xs text-[#857078]">{c.phone ? `+91 ${c.phone}` : "—"}</p>
                </td>
                <td className="px-4 py-3 space-x-1">{badge(c.email_verified, "Email ✓", "Email ✗")}{badge(c.phone_verified, "Phone ✓", "Phone ✗")}</td>
                <td className="px-4 py-3 text-xs text-[#45303A]">{c.auth_method === "google" ? "Google" : "Email"}</td>
                <td className="px-4 py-3">{badge(c.account_status !== "deactivated", "Active", "Deactivated")}</td>
                <td className="px-4 py-3 text-xs text-[#857078]">{c.created_at ? new Date(c.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "—"}</td>
                <td className="px-4 py-3 text-xs text-[#857078]">{c.last_login_at ? new Date(c.last_login_at).toLocaleDateString("en-IN", { day: "numeric", month: "short" }) : "—"}</td>
                <td className="px-4 py-3 text-xs text-[#45303A]">{c.order_count}</td>
                <td className="px-4 py-3 text-xs font-medium text-[#2E1A22]">{inr(c.total_spent)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {detail && (
        <div className="fixed inset-0 z-[80] bg-black/50 backdrop-blur-sm overflow-y-auto" onClick={() => setDetail(null)} data-testid="customer-detail">
          <div className="max-w-2xl mx-auto my-10 bg-white rounded-2xl shadow-2xl p-6 sm:p-8" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between">
              <div>
                <p className="font-mono-lux text-xs text-[#7E1E3F]">{detail.customer.customer_id}</p>
                <h3 className="font-serif-lux text-2xl text-[#2E1A22] mt-1">{detail.customer.name}</h3>
                <p className="text-sm text-[#857078]">{detail.customer.email}{detail.customer.phone ? ` · +91 ${detail.customer.phone}` : ""}</p>
              </div>
              <button onClick={() => setDetail(null)} className="text-[#857078]" data-testid="customer-detail-close">✕</button>
            </div>
            <div className="grid grid-cols-2 gap-3 mt-6 text-sm">
              {[["Status", detail.customer.account_status === "deactivated" ? "Deactivated" : "Active"],
                ["Sign-in Method", detail.customer.auth_method === "google" ? "Google" : "Email & Password"],
                ["Email Verified", detail.customer.email_verified ? "Yes" : "No"],
                ["Phone Verified", detail.customer.phone_verified ? "Yes" : "No"],
                ["Registered", detail.customer.created_at ? new Date(detail.customer.created_at).toLocaleString("en-IN") : "—"],
                ["Last Login", detail.customer.last_login_at ? new Date(detail.customer.last_login_at).toLocaleString("en-IN") : "Never"]].map(([k, v]) => (
                <div key={k} className="bg-[#FBF7F1] rounded-xl p-3">
                  <p className="text-[10px] font-mono-lux tracking-widest uppercase text-[#857078]">{k}</p>
                  <p className="text-[#2E1A22] mt-0.5">{v}</p>
                </div>
              ))}
            </div>
            <div className="mt-6">
              <p className="text-[10px] font-mono-lux tracking-widest uppercase text-[#857078] mb-2">Orders ({detail.orders.length}) · {inr(detail.orders.reduce((a, o) => a + o.total, 0))} total</p>
              <div className="space-y-2 max-h-44 overflow-y-auto">
                {detail.orders.length === 0 && <p className="text-xs text-[#857078]">No orders yet.</p>}
                {detail.orders.map((o) => (
                  <div key={o.order_id} className="flex justify-between text-xs border border-[#E5E7EB] rounded-lg px-3 py-2">
                    <span className="font-mono-lux text-[#7E1E3F]">{o.order_id}</span>
                    <span className="text-[#857078]">{o.status}</span>
                    <span className="text-[#2E1A22] font-medium">{inr(o.total)}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="mt-5">
              <p className="text-[10px] font-mono-lux tracking-widest uppercase text-[#857078] mb-2">Saved Addresses ({detail.addresses.length})</p>
              {detail.addresses.length === 0 && <p className="text-xs text-[#857078]">None saved.</p>}
              {detail.addresses.map((a) => (
                <p key={a.id} className="text-xs text-[#45303A] border border-[#E5E7EB] rounded-lg px-3 py-2 mb-2">
                  {a.name}, {a.house}, {a.street}, {a.city}, {a.district}, {a.state} – {a.pincode}{a.is_default ? " · Default" : ""}
                </p>
              ))}
            </div>
            <button onClick={() => toggleStatus(detail.customer)}
              className={`mt-6 w-full text-xs font-semibold tracking-wider uppercase rounded-full py-3.5 transition-colors ${detail.customer.account_status === "deactivated" ? "bg-[#2A9D8F] text-white" : "bg-[#E63946]/10 text-[#E63946] border border-[#E63946]/40 hover:bg-[#E63946]/20"}`}
              data-testid="customer-toggle-status">
              {detail.customer.account_status === "deactivated" ? "Activate Account" : "Deactivate Account"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

